import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateJournalEntryInput,
  JournalEntry,
  JournalEntryWithLines,
  ReclassifyJournalEntryInput,
  UpdateJournalEntryInput,
} from '@accounting/shared';
import { api } from '../lib/api';
import { accountsKey } from './useAccounts';

export const journalEntriesKey = (companyId: string | null) =>
  ['journal-entries', companyId ?? 'none'] as const;

/**
 * Returns `{ entries, total }` — `total` is the unpaged match count, which the
 * pager needs to know whether a next page exists.
 */
export function useJournalEntries(
  companyId: string | null,
  opts: {
    includeVoid?: boolean;
    limit?: number;
    offset?: number;
    /** Only entries with at least one line hitting this account. */
    accountId?: string;
  } = { includeVoid: true },
) {
  const params = new URLSearchParams();
  if (opts.includeVoid) params.set('includeVoid', 'true');
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.offset != null) params.set('offset', String(opts.offset));
  if (opts.accountId) params.set('accountId', opts.accountId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: [
      ...journalEntriesKey(companyId),
      opts.includeVoid ?? false,
      opts.limit ?? null,
      opts.offset ?? null,
      opts.accountId ?? null,
    ] as const,
    enabled: companyId !== null,
    // Keeps the current page on screen while the next one loads instead of
    // flashing an empty table on every page change.
    placeholderData: (prev) => prev,
    queryFn: () =>
      api.get<{ entries: JournalEntry[]; total: number }>(
        `/companies/${companyId}/journal-entries${qs}`,
      ),
  });
}

export function useJournalEntry(
  companyId: string | null,
  entryId: string | null,
) {
  return useQuery({
    queryKey: ['journal-entry', companyId ?? 'none', entryId ?? 'none'],
    enabled: companyId !== null && entryId !== null,
    queryFn: () =>
      api
        .get<{ entry: JournalEntryWithLines }>(
          `/companies/${companyId}/journal-entries/${entryId}`,
        )
        .then((r) => r.entry),
  });
}

export function useCreateJournalEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJournalEntryInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ entry: JournalEntryWithLines }>(
          `/companies/${companyId}/journal-entries`,
          input,
        )
        .then((r) => r.entry);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: journalEntriesKey(companyId) });
      qc.invalidateQueries({ queryKey: accountsKey(companyId) });
    },
  });
}

export function useUpdateJournalEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, input }: { entryId: string; input: UpdateJournalEntryInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ entry: JournalEntryWithLines }>(
          `/companies/${companyId}/journal-entries/${entryId}`,
          input,
        )
        .then((r) => r.entry);
    },
    onSuccess: (_data, { entryId }) => {
      qc.invalidateQueries({ queryKey: journalEntriesKey(companyId) });
      qc.invalidateQueries({ queryKey: ['journal-entry', companyId ?? 'none', entryId] });
      qc.invalidateQueries({ queryKey: accountsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['command-center'] });
    },
  });
}

/**
 * Re-account an entry without unwinding its reconciliation. Invalidates the
 * same set as an edit — the entry's composition changed, so every report and
 * account balance downstream of it is stale.
 */
export function useReclassifyJournalEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      input,
    }: {
      entryId: string;
      input: ReclassifyJournalEntryInput;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ entry: JournalEntryWithLines }>(
          `/companies/${companyId}/journal-entries/${entryId}/reclassify`,
          input,
        )
        .then((r) => r.entry);
    },
    onSuccess: (_data, { entryId }) => {
      qc.invalidateQueries({ queryKey: journalEntriesKey(companyId) });
      qc.invalidateQueries({ queryKey: ['journal-entry', companyId ?? 'none', entryId] });
      qc.invalidateQueries({ queryKey: accountsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['command-center'] });
      qc.invalidateQueries({ queryKey: ['books-health'] });
    },
  });
}

export function useVoidJournalEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { entryId: string; voidReason: string; override?: boolean }) => {
      if (!companyId) throw new Error('No active company');
      return api.post(
        `/companies/${companyId}/journal-entries/${input.entryId}/void`,
        {
          voidReason: input.voidReason,
          ...(input.override ? { override: true } : {}),
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: journalEntriesKey(companyId) });
      qc.invalidateQueries({ queryKey: accountsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['journal-duplicates', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
    },
  });
}

// ── Bulk mark/unmark as reconciled (historical catch-up from imports) ──────

interface BulkMarkResult {
  marked: number;
  alreadyLocked: number;
  notFound: number;
}
interface BulkUnmarkResult {
  unmarked: number;
  skippedFormallyReconciled: number;
  notFound: number;
}

export function useBulkMarkReconciled(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryIds: string[]) => {
      if (!companyId) throw new Error('No active company');
      return api.post<BulkMarkResult>(
        `/companies/${companyId}/journal-entries/mark-reconciled`,
        { entryIds },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: journalEntriesKey(companyId) });
      qc.invalidateQueries({ queryKey: ['report'] });
      // Re-bake bank-match candidates since locked entries no longer qualify.
      qc.invalidateQueries({ queryKey: ['match-candidates'] });
      qc.invalidateQueries({ queryKey: ['match-candidates-bulk'] });
    },
  });
}

export function useBulkUnmarkReconciled(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryIds: string[]) => {
      if (!companyId) throw new Error('No active company');
      return api.post<BulkUnmarkResult>(
        `/companies/${companyId}/journal-entries/unmark-reconciled`,
        { entryIds },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: journalEntriesKey(companyId) });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['match-candidates'] });
      qc.invalidateQueries({ queryKey: ['match-candidates-bulk'] });
    },
  });
}

/**
 * Hard delete a journal entry. The General Ledger page walks the user
 * through escalating warnings (cleared = one warning; reconciled = two
 * warnings) before calling this. Server doesn't enforce a "must void
 * instead" gate because the use case is true duplicate cleanup.
 */
export function useDeleteJournalEntry(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<{ deleted: boolean; entryNumber: string }>(
        `/companies/${companyId}/journal-entries/${entryId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: journalEntriesKey(companyId) });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['command-center'] });
      // Re-bake match candidates since a previously matched JE may have
      // released its bank tx back to the unmatched pool.
      qc.invalidateQueries({ queryKey: ['match-candidates'] });
      qc.invalidateQueries({ queryKey: ['match-candidates-bulk'] });
      qc.invalidateQueries({ queryKey: ['bank-transactions'] });
    },
  });
}
